import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { GROWTH } from '../content/siteContent';
import { useReducedMotion, useIsDesktop } from '../hooks/useMotionPrefs';

const MILESTONE_POINTS = [
  { x: 60, y: 520 },
  { x: 340, y: 468 },
  { x: 580, y: 396 },
  { x: 830, y: 288 },
  { x: 1150, y: 130 },
];

export default function Growth() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (reduced) {
      ref.current.querySelector('.growth-line').style.strokeDashoffset = 0;
      ref.current.querySelectorAll('.growth-milestone, .growth-label').forEach((n) => (n.style.opacity = 1));
      return;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 65%',
          toggleActions: 'play none none none',
        },
      });
      tl.timeScale(0.6);
      // the line draws itself upward — cinematic growth, not a finance chart
      tl.fromTo('.growth-line', { strokeDashoffset: 1 }, { strokeDashoffset: 0, ease: 'none', duration: 0.8 }, 0.05);
      GROWTH.milestones.forEach((_, i) => {
        const t = 0.1 + i * 0.16;
        tl.fromTo(
          `.growth-milestone-${i}`,
          { opacity: 0, scale: 0, transformOrigin: 'center' },
          { opacity: 1, scale: 1, duration: 0.06 },
          t
        ).fromTo(`.growth-label-${i}`, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.08 }, t + 0.04);
      });
      tl.fromTo('.growth-final', { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.16 }, 0.86);
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="growth"
      data-testid="growth-section"
      className="relative h-screen overflow-hidden bg-[#000000]"
    >
      <div className="absolute left-5 top-24 z-10 md:left-12 md:top-28">
        <SectionTag number="10" label="Scale & Growth" />
        <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-white md:text-4xl">
          {GROWTH.heading}
        </h2>
      </div>

      <svg viewBox={isDesktop ? '0 0 1240 640' : '40 60 1140 540'} preserveAspectRatio="xMidYMid meet" className="absolute inset-0 h-full w-full" aria-hidden="true">
        <path
          className="growth-line energy-glow"
          d="M 60 520 C 240 505 320 490 340 468 C 420 385 520 410 580 396 C 680 372 740 300 830 288 C 950 272 1040 160 1150 130"
          fill="none"
          stroke="#47a0d9"
          strokeWidth="2.2"
          pathLength={1}
          strokeDasharray={1}
          strokeDashoffset={1}
        />
        {MILESTONE_POINTS.map((p, i) => (
          <g key={i}>
            <circle className={`growth-milestone growth-milestone-${i}`} cx={p.x} cy={p.y} r="6" fill="#050505" stroke="#47a0d9" strokeWidth="2" />
            <text
              className={`growth-label growth-label-${i} fill-white/55`}
              x={p.x}
              y={p.y - 22}
              fontSize="13"
              letterSpacing="2.5"
              textAnchor="middle"
            >
              {GROWTH.milestones[i].toUpperCase()}
            </text>
          </g>
        ))}
      </svg>

      <div className="growth-final absolute bottom-12 left-5 right-5 md:left-12">
        <p className="font-display text-xl font-semibold tracking-tight text-white md:text-3xl">
          And this is only the beginning<span className="text-[#47a0d9]">.</span>
        </p>
        <p className="mt-4 max-w-md text-[11px] uppercase tracking-[0.22em] text-white/40">{GROWTH.note}</p>
      </div>
    </section>
  );
}
